// frontend/src/App.js
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import { BackgroundTaskProvider } from './contexts/BackgroundTaskContext';
import BackgroundTaskNotification from './components/BackgroundTaskNotification';
import BackgroundJobPoller from './components/BackgroundJobPoller';
import DashboardOverview from './components/DashboardOverview';

// Eager load only the Dashboard (most visited page) and Navbar
import Dashboard from './pages/Dashboard';
import MonthlyDigestDetailed from './pages/MonthlyDigest-Detailed';
import Footer from './components/Footer';

// Lazy load all other pages for code splitting
const About = lazy(() => import('./pages/About'));
const Help = lazy(() => import('./pages/help/Help'));
const MetadataTable = lazy(() => import('./pages/MetadataTable'));
const AnalysisPage = lazy(() => import('./pages/Analysis'));
const OutbreakDashboard = lazy(() => import('./pages/OutbreakDashboard'));
const SurveillanceDashboard = lazy(() => import('./pages/SurveillanceDashboard'));
const MutationAnalysis = lazy(() => import('./pages/MutationAnalysis'));
const PandemicRisk = lazy(() => import('./pages/PandemicRisk'));
const LaboratorySupport = lazy(() => import('./pages/LaboratorySupport'));
const BatchComparison = lazy(() => import('./pages/BatchComparison'));
const PhyloMutationTracking = lazy(() => import('./pages/PhyloMutationTracking'));

// Import CSS files
import './App.css';

// Loading component for Suspense fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '80vh',
    fontSize: '18px',
    color: '#6b7280'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }}></div>
      <p>Loading...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);
// Import surveillance components if they exist, with fallback error boundaries
let ErrorBoundary, useToast;

try {
  ErrorBoundary = require('./components/surveillance/ErrorBoundary').default;
  const toastModule = require('./components/surveillance/AlertNotification');
  useToast = toastModule.useToast;
} catch (error) {
  console.warn('Surveillance components not found, using fallback error boundary');
  
  // Fallback ErrorBoundary component
  class FallbackErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      console.error('Application Error:', error, errorInfo);
      if (this.props.onError) {
        this.props.onError(error, errorInfo);
      }
    }

    render() {
      if (this.state.hasError) {
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb'
          }}>
            <h1 style={{ color: '#dc2626', marginBottom: '16px' }}>
              {this.props.title || 'Application Error'}
            </h1>
            <p style={{ color: '#6b7280', marginBottom: '24px', maxWidth: '600px' }}>
              {this.props.message || 'An unexpected error occurred. Please refresh the page.'}
            </p>
            {this.props.showRefreshHint && (
              <button 
                onClick={() => window.location.reload()} 
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Refresh Page
              </button>
            )}
          </div>
        );
      }

      return this.props.children;
    }
  }

  ErrorBoundary = FallbackErrorBoundary;
  
  // Fallback toast hook
  useToast = () => ({
    ToastContainer: ({ position }) => (
      <div id="toast-container" style={{
        position: 'fixed',
        top: position?.includes('top') ? '20px' : 'auto',
        bottom: position?.includes('bottom') ? '20px' : 'auto',
        right: position?.includes('right') ? '20px' : 'auto',
        left: position?.includes('left') ? '20px' : 'auto',
        zIndex: 9999
      }} />
    )
  });
}

function App() {
  const { ToastContainer } = useToast();
  const [showOverviewPopup, setShowOverviewPopup] = useState(() => {
    // Check if overview popup was shown in this session
    const popupShown = sessionStorage.getItem('overviewPopupShown');
    return !popupShown; // Default to true (show on first visit)
  });

  const handleCloseOverviewPopup = () => {
    setShowOverviewPopup(false);
    // Mark popup as shown in this session
    sessionStorage.setItem('overviewPopupShown', 'true');
  };

  // Listen for custom event to show overview from navbar button
  useEffect(() => {
    const handleShowOverview = () => {
      setShowOverviewPopup(true);
      // Remove the sessionStorage flag so popup shows
      sessionStorage.removeItem('overviewPopupShown');
    };

    window.addEventListener('showDashboardOverview', handleShowOverview);
    return () => window.removeEventListener('showDashboardOverview', handleShowOverview);
  }, []);

  return (
    <ErrorBoundary
      title="H5N1 Surveillance Dashboard Error"
      message="The H5N1 Surveillance Dashboard encountered an unexpected error. Please refresh the page or contact support if the problem persists."
      showRefreshHint={true}
      onError={(error, errorInfo) => {
        console.error('Application Error:', error, errorInfo);
      }}
    >
      <BackgroundTaskProvider>
        <Router>
          <div className="App">
            {/* Navigation Bar */}
            <Navbar />

            {/* Global Dashboard Overview Modal - accessible from all pages */}
            {showOverviewPopup && (
              <Suspense fallback={null}>
                <DashboardOverview onClose={handleCloseOverviewPopup} />
              </Suspense>
            )}

            {/* Background Task Notification */}
            <BackgroundTaskNotification />

            {/* Global Background Job Poller - polls jobs across all pages */}
            <BackgroundJobPoller />

            {/* Main Application Routes with Suspense for lazy loading */}
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Default redirect to dashboard */}
              <Route
                path="/"
                element={<Navigate to="/dashboard" replace />}
              />

              {/* Primary dashboard - H5N1 visualization dashboard */}
              <Route
                path="/dashboard"
                element={<Dashboard />}
              />

              {/* Surveillance dashboard with nested routing */}
              <Route
                path="/surveillance/*"
                element={<SurveillanceDashboard />}
              />

              {/* Legacy route support for SurveillanceDashboard */}
              <Route
                path="/SurveillanceDashboard"
                element={<Navigate to="/surveillance" replace />}
              />

              {/* Analysis tools and phylogenetic analysis */}
              <Route
                path="/analysis"
                element={<AnalysisPage />}
              />

              {/* Legacy route support */}
              <Route
                path="/Analysis"
                element={<Navigate to="/analysis" replace />}
              />

              {/* Outbreak monitoring dashboard */}
              <Route
                path="/outbreak"
                element={<OutbreakDashboard />}
              />

              {/* Legacy route support */}
              <Route
                path="/OutbreakDashboard"
                element={<Navigate to="/outbreak" replace />}
              />

              {/* Metadata table and data management */}
              <Route
                path="/metadata"
                element={<MetadataTable />}
              />

              {/* Legacy route support */}
              <Route
                path="/MetadataTable"
                element={<Navigate to="/metadata" replace />}
              />

              {/* About page */}
              <Route
                path="/about"
                element={<About />}
              />

              {/* Legacy route support */}
              <Route
                path="/About"
                element={<Navigate to="/about" replace />}
              />

              {/* Help page */}
              <Route
                path="/help"
                element={<Help />}
              />

              {/* Legacy route support */}
              <Route
                path="/Help"
                element={<Navigate to="/help" replace />}
              />

              {/* Advanced Analysis Pages */}
              <Route
                path="/mutation-analysis"
                element={<MutationAnalysis />}
              />

              <Route
                path="/pandemic-risk"
                element={<PandemicRisk />}
              />

              <Route
                path="/laboratory-support"
                element={<LaboratorySupport />}
              />

              <Route
                path="/batch-comparison"
                element={<BatchComparison />}
              />

              <Route
                path="/dev-mut"
                element={<PhyloMutationTracking />}
              />

              <Route
                path="/monthly-digest-detailed"
                element={<MonthlyDigestDetailed />}
              />

              {/* Direct access to specific analysis tools */}
              <Route
                path="/mrca-levels"
                element={<Navigate to="/analysis?tool=MRCAFinderLevels" replace />}
              />

              {/* Catch-all route - redirect to dashboard */}
              <Route
                path="*"
                element={<Navigate to="/dashboard" replace />}
              />
            </Routes>
          </Suspense>

          {/* Global Toast Container for notifications */}
          <ToastContainer position="top-right" />

          {/* Global Footer */}
          <Footer />
        </div>
        </Router>
      </BackgroundTaskProvider>
    </ErrorBoundary>
  );
}

export default App;
